import {z} from 'zod';

import {
  AudienceSchema,
  CAROUSEL_SLIDE_ROLES,
  CAROUSEL_SLIDES,
  CtaObjectiveSchema,
  EditorialSeriesSchema,
  PieceKindSchema,
} from './editorial.js';
import {UuidV4Schema} from './common.js';
import {
  EDITORIAL_PROSE_MAX_CHARS,
  EditorialFindingSchema,
  EditorialPendencySchema,
  EditorialRepetitionSchema,
  EditorialSourceSupportSchema,
} from './editorial-limits.js';

/**
 * Contratos da geração de conteúdo.
 *
 * A regra que organiza este arquivo: **saída de modelo é entrada não
 * confiável**. O texto que volta de um gerador passa exatamente pelas mesmas
 * cercas que passaria um corpo de requisição vindo da internet, porque é o que
 * ele é: texto produzido por algo que leu material de fora.
 *
 * Por isso a estrutura é fechada (`strictObject`), os limites são numéricos e
 * verificáveis, e nada aqui aceita "o modelo disse que estava tudo bem".
 */

// ---------------------------------------------------------------------------
// Reivindicação
// ---------------------------------------------------------------------------

/**
 * Uma afirmação feita pela peça.
 *
 * `isQuantitative` é o que separa "automação economiza tempo" de "automação
 * economiza 40% do tempo". A primeira é opinião e não precisa de prova; a
 * segunda é número, e número sem evidência é invenção com aparência de dado.
 */
export const GeneratedClaimSchema = z
  .strictObject({
    text: z.string().trim().min(1).max(400),
    isQuantitative: z.boolean(),
    /** Aponta para um snapshot que já existe. O gerador não cria evidência. */
    evidenceSnapshotId: UuidV4Schema.nullable(),
  })
  .refine(
    (claim) => !claim.isQuantitative || claim.evidenceSnapshotId !== null,
    {
      message: 'afirmação quantitativa exige evidência',
      path: ['evidenceSnapshotId'],
    },
  );
export type GeneratedClaim = z.infer<typeof GeneratedClaimSchema>;

// ---------------------------------------------------------------------------
// Conteúdo gerado
// ---------------------------------------------------------------------------

/** Um trecho do roteiro, com o texto que aparece na tela naquele momento. */
export const ScriptSegmentSchema = z.strictObject({
  /** Ordem de exibição, começando em 1. */
  order: z.int().positive().max(12),
  /** Fala do roteiro. */
  speech: z.string().trim().min(1).max(600),
  /** Texto de tela. Curto de propósito: legenda cheia não se lê no Reel. */
  screenText: z.string().trim().min(1).max(90),
  /** Duração pretendida do trecho. */
  durationMs: z.int().min(500).max(20_000),
});
export type ScriptSegment = z.infer<typeof ScriptSegmentSchema>;

export const MAX_CTA_PER_PIECE = 1;

/**
 * Teto da legenda, que é o do Instagram.
 *
 * Vira constante porque agora ele é cobrado em DOIS lugares: no schema, contra
 * o que o gerador escreveu, e no aviso de imagem gerada por IA, que acrescenta
 * uma linha depois da validação. Dois números escritos à mão divergiriam, e o
 * lado que ficasse maior produziria uma legenda que a Meta recusa.
 */
export const CAPTION_MAX_CHARS = 2_200;

/**
 * Palavras por dica do carrossel, e por que o número está em dois lugares.
 *
 * Doze, e é o pedido do dono. Ele aparece aqui, no schema que RECUSA, e em
 * `PALAVRAS_POR_DICA` no domínio, que é onde o P12 e o renderizador o leem. Os
 * dois pacotes não podem importar um do outro (o contrato não depende do
 * domínio), e o teste do domínio compara os dois números.
 */
export const WORDS_PER_TIP = 12;

export const GeneratedContentSchema = z
  .strictObject({
    pieceKind: PieceKindSchema,
    series: EditorialSeriesSchema,
    audience: AudienceSchema,
    objective: CtaObjectiveSchema,
    hook: z.string().trim().min(1).max(120),
    segments: z.array(ScriptSegmentSchema).min(2).max(12),
    caption: z.string().trim().min(1).max(CAPTION_MAX_CHARS),
    /**
     * A lista de CTA existe no plural para que o limite seja VERIFICÁVEL. Um
     * campo único esconderia a segunda chamada dentro da legenda, e ninguém
     * conseguiria medir. Aqui, passar de um é erro de schema.
     */
    ctas: z.array(z.string().trim().min(1).max(160)).max(MAX_CTA_PER_PIECE),
    claims: z.array(GeneratedClaimSchema).max(10),
    /** Hashtags sem o `#`, para não virar duas convenções no mesmo campo. */
    hashtags: z
      .array(
        z
          .string()
          .trim()
          .regex(/^[\p{L}\p{N}_]{2,40}$/u, 'hashtag sem # e sem espaço'),
      )
      .max(8),
  })
  .refine(
    (content) =>
      new Set(content.segments.map((s) => s.order)).size ===
      content.segments.length,
    {
      message: 'a ordem dos trechos precisa ser distinta',
      path: ['segments'],
    },
  )
  /*
   * O CARROSSEL tem exatamente sete trechos, e cada um é um slide.
   *
   * Ele reusa `segments` em vez de ganhar um campo próprio, e a economia não é
   * o motivo: `screenText` já é o texto DESENHADO na tela, que é exatamente o
   * que um slide é, e a esteira inteira já o carrega até os portões
   * (`screenTexts`), até o renderizador e até o crítico visual. Um campo
   * paralelo obrigaria os quatro a aprender a segunda forma, e no dia em que só
   * três aprendessem a peça sairia com um texto aprovado e outro desenhado.
   *
   * `speech` é a FRASE DE APOIO do slide, e o P12 a pede com todas as letras:
   * "quando uma dica exigir contexto, acrescentar uma frase curta de apoio, sem
   * esconder condições importantes para caber no limite".
   *
   * Sete, e não "pelo menos sete": a capa é o slide 1, o fechamento é o 7, e o
   * que estiver entre eles são as cinco dicas. Um oitavo trecho não teria papel
   * nenhum na sequência, e um sexto deixaria o fechamento no lugar de uma dica.
   */
  .refine(
    (content) =>
      content.pieceKind !== 'carousel' ||
      content.segments.length === CAROUSEL_SLIDES,
    {
      message: `o carrossel tem exatamente ${CAROUSEL_SLIDES} slides: capa, cinco dicas e fechamento`,
      path: ['segments'],
    },
  )
  /*
   * A régua das DOZE PALAVRAS por dica, cobrada aqui e não só no prompt.
   *
   * O pedido do dono é textual: "5 ações práticas de até 12 palavras cada". Ela
   * mora no schema pelo motivo de sempre nesta casa: prompt é pedido, schema é
   * cerca. Uma dica de dezoito palavras não é um modelo desobediente que
   * alguém corrige na revisão, é um texto que o renderizador vai encolher até o
   * degrau mais baixo da escada para caber no slide.
   *
   * Ela cobre as CINCO DICAS, e não a capa nem o fechamento: a capa carrega um
   * benefício e o fechamento carrega a chamada, e os dois têm desenho próprio
   * com espaço próprio. O limite de 90 caracteres de `screenText` continua
   * valendo para os sete.
   */
  .refine(
    (content) =>
      content.pieceKind !== 'carousel' ||
      content.segments
        .filter((s) => s.order > 1 && s.order < CAROUSEL_SLIDES)
        .every(
          (s) => s.screenText.trim().split(/\s+/u).length <= WORDS_PER_TIP,
        ),
    {
      message: `cada dica do carrossel tem no máximo ${WORDS_PER_TIP} palavras`,
      path: ['segments'],
    },
  );
export type GeneratedContent = z.infer<typeof GeneratedContentSchema>;

// ---------------------------------------------------------------------------
// Entrada e resultado
// ---------------------------------------------------------------------------

/**
 * Evidência já SANITIZADA, do jeito que ela pode ser mostrada ao gerador.
 *
 * Não existe campo para o corpo bruto da fonte de propósito. O que vai para o
 * gerador é um excerto curto que alguém escolheu, e não a página inteira: é
 * assim que texto de terceiro para de chegar com espaço para carregar
 * instrução.
 */
export const SanitizedEvidenceSchema = z.strictObject({
  evidenceSnapshotId: UuidV4Schema,
  title: z.string().min(1).max(300),
  excerpt: z.string().min(1).max(1_200),
  publishedAt: z.string().nullable(),
});
export type SanitizedEvidence = z.infer<typeof SanitizedEvidenceSchema>;

/**
 * A CORREÇÃO editorial, quando ela existe.
 *
 * É entrada montada por nós a partir de um parecer que já passou pelo schema
 * dele, e não texto livre. Ela viaja no pedido do escritor com a mesma etiqueta
 * das fontes: dado, nunca instrução. O que impede um achado plantado de mudar a
 * peça não é essa etiqueta, é `fixarAncoras` no serviço de geração, que
 * sobrescreve série, público, objetivo e formato com os valores da pauta
 * aprovada depois que o modelo responde.
 *
 * O campo é opcional, e a geração inicial nunca o manda. Uma peça corrigida é
 * uma segunda chamada, com o mesmo contrato e a mesma validação.
 *
 * ## O que ela carrega, e a ordem de importância
 *
 * Primeiro o MOTIVO que reprovou a peça: pendência factual, gancho que promete
 * além da fonte, repetição de assunto e forma. Os três derrubam a peça
 * independentemente da nota, e até 2026-09-07 nenhum deles chegava aqui. Depois
 * a mudança principal e os achados, que são a crítica de detalhe.
 *
 * Sem os três primeiros, uma reescrita paga sai contra um problema que quem
 * reescreve não consegue ver, e o resultado dela cai no mesmo veto.
 */
export const GenerationRevisionSchema = z.strictObject({
  /*
   * O MESMO schema do achado do parecer, e não uma cópia com outro teto.
   *
   * Era uma cópia escrita à mão, com trezentos caracteres onde o parecer aceita
   * mil. O achado F07 da auditoria de 2026-09-06 mediu a consequência: um achado
   * VÁLIDO de 400 caracteres passava como parecer e era recusado como pedido de
   * correção, então a correção de um parecer legítimo morria no schema ANTES de
   * a chamada paga ao escritor sair. O achado atravessa a esteira inteiro, e não
   * cortado: truncar aqui esconderia a segunda metade do que o crítico escreveu.
   */
  findings: z.array(EditorialFindingSchema).max(10),
  mainChange: z
    .string()
    .trim()
    .min(1)
    .max(EDITORIAL_PROSE_MAX_CHARS)
    .nullable(),
  /**
   * A PENDÊNCIA factual, que é o MOTIVO pelo qual a peça foi reprovada.
   *
   * Ela não estava aqui até 2026-09-07, e a ausência dela era o defeito central
   * deste contrato. A medição de produção: treze pareceres de texto, todos da
   * versão `devmil-editorial-p05-2026-09-c`, nenhum passando no piso, e o mesmo
   * veto reprovando os treze, pendência factual crítica em 13 de 13. As notas não
   * eram o problema, cinco dos treze já alcançavam a soma de 16.
   *
   * O crítico identificava a pendência, a pendência derrubava a peça, e o
   * escritor nunca ficava sabendo dela: a correção levava achado, mudança
   * principal e módulos, e mais nada. A reescrita era estruturalmente incapaz de
   * consertar a única coisa que reprovava todas as peças.
   *
   * Ela viaja INTEIRA, com a gravidade, e não achatada num achado. `critical` e
   * `minor` pedem reações diferentes de quem reescreve: a crítica é a razão da
   * reprovação e tem que sair resolvida da reescrita; a menor é um ajuste que
   * não reprovou nada. Sem o campo, as duas chegariam como a mesma frase.
   *
   * Nulo é a resposta honesta de um parecer sem pendência, e não a omissão de
   * uma que existe.
   */
  pendency: EditorialPendencySchema.nullable(),
  /**
   * O gancho promete mais do que a fonte sustenta.
   *
   * Preenchido SÓ quando o veredito reprova (`overstated`, 3 dos 13 pareceres
   * medidos). `supported`, `no_sources` e `partially_supported` chegariam ao
   * escritor como "está tudo bem com este item", que é ruído dentro de um pedido
   * cujo assunto inteiro é o que precisa mudar: a correção carrega o que reprovou
   * a peça, e não o boletim completo da conferência.
   *
   * `partially_supported` é o caso de fronteira, e ele fica de fora de propósito:
   * a régua decidiu que ele NÃO derruba (é o estado normal de uma peça que
   * generaliza um pouco além do caso citado), e mandá-lo pediria reescrita quase
   * sempre. Se a calibração mostrar que valia a pena mandar, é uma linha aqui.
   */
  sourceSupport: EditorialSourceSupportSchema.nullable(),
  /**
   * A peça repete o assunto E a forma de uma recente.
   *
   * Preenchido SÓ quando o veredito reprova (`both`, 5 dos 13 pareceres
   * medidos), pela mesma regra do `sourceSupport`. Repetir só o assunto ou só a
   * forma não derruba, e a série existe justamente para voltar ao mesmo assunto.
   *
   * `reference` viaja junto porque "não repita" sem dizer O QUE repetiu é um
   * pedido que ninguém consegue atender.
   */
  repetition: EditorialRepetitionSchema.nullable(),
  /**
   * Os módulos que entram neste pedido, escolhidos por regra e não pelo modelo.
   *
   * Lista fechada de propósito: são os dois módulos de ESCRITA do catálogo. Uma
   * lista aberta deixaria o dia em que alguém mandasse um módulo mensal junto
   * de cada correção, e o pedido triplicaria de tamanho sem ninguém decidir.
   */
  modules: z.array(z.enum(['P09', 'P10'])).max(2),
});
export type GenerationRevision = z.infer<typeof GenerationRevisionSchema>;

export const GenerationInputSchema = z.strictObject({
  topicTitle: z.string().trim().min(1).max(200),
  premise: z.string().trim().min(1).max(2_000),
  pieceKind: PieceKindSchema,
  series: EditorialSeriesSchema,
  audience: AudienceSchema,
  objective: CtaObjectiveSchema,
  voiceGuide: z.string().min(1).max(4_000),
  policyVersion: z.string().min(1).max(60),
  evidence: z.array(SanitizedEvidenceSchema).max(8),
  revision: GenerationRevisionSchema.optional(),
});
export type GenerationInput = z.infer<typeof GenerationInputSchema>;

/**
 * Uso e custo da geração.
 *
 * `costMicros` é inteiro em micros, um milionésimo da moeda, para somar sem
 * acumular erro de ponto flutuante.
 *
 * Ele é ANULÁVEL de propósito. Quando a tabela de preço do modelo não está
 * configurada, o custo é desconhecido, e desconhecido não é zero: gravar zero
 * faria o relatório de gasto somar tranquilamente uma conta que ninguém mediu.
 * Zero só aparece aqui quando zero é o custo de verdade, como no gerador de
 * fixture, que não faz chamada nenhuma.
 */
export const GenerationUsageSchema = z.strictObject({
  inputTokens: z.int().nonnegative(),
  outputTokens: z.int().nonnegative(),
  costMicros: z.int().nonnegative().nullable(),
});
export type GenerationUsage = z.infer<typeof GenerationUsageSchema>;

export const GenerationResultSchema = z.strictObject({
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(80),
  promptVersion: z.string().min(1).max(60),
  output: GeneratedContentSchema,
  /** Hash da entrada e da saída. O texto bruto não é guardado. */
  inputHash: z.string().regex(/^[0-9a-f]{64}$/u),
  outputHash: z.string().regex(/^[0-9a-f]{64}$/u),
  usage: GenerationUsageSchema,
  latencyMs: z.int().nonnegative(),
});
export type GenerationResult = z.infer<typeof GenerationResultSchema>;

/**
 * O adaptador de geração.
 *
 * Existe para que trocar de provedor seja trocar uma implementação, e não
 * reescrever a esteira. O serviço aplica as mesmas cercas aos dois: fixture e
 * provedor remoto passam pelo mesmo schema e pelos mesmos limites, senão o
 * teste offline provaria uma coisa e a produção faria outra.
 */
export interface TextGenerator {
  readonly provider: string;
  generate(input: GenerationInput): Promise<GenerationResult>;
}

// ---------------------------------------------------------------------------
// Imagem gerada
// ---------------------------------------------------------------------------

/**
 * O que o gerador de imagem recebe.
 *
 * Só campos que JÁ passaram pelo schema do conteúdo, e nenhum texto livre além
 * deles. O prompt é montado do outro lado por uma moldura fixa nossa: mandar
 * daqui um campo aberto seria abrir uma segunda porta para texto de fonte
 * chegar ao provedor sem passar pelas cercas da geração de texto.
 */
/**
 * Teto do texto do slide que entra no pedido de imagem.
 *
 * Ele é a SOMA de `screenText` (90) e `speech` (600) de um trecho, cortada: a
 * esteira junta os dois porque uma dica de doze palavras sozinha às vezes não
 * diz o que ilustrar, e a soma crua passaria dos 690 caracteres.
 *
 * Constante exportada, e não um número escrito nos dois lugares: quem corta é a
 * esteira, quem recusa é este schema, e dois números divergiriam no dia em que
 * alguém mexesse num deles.
 *
 * A moldura do prompt corta DE NOVO, em 200 (`TETO_DO_TEXTO_DO_SLIDE`), e isso
 * não é redundância: aquele é o teto do que vale a pena mandar ao provedor, e
 * este é o teto do que o contrato aceita carregar.
 */
export const IMAGE_SLIDE_TEXT_MAX = 500;

export const ImageGenerationInputSchema = z.strictObject({
  topicTitle: z.string().trim().min(1).max(200),
  hook: z.string().trim().min(1).max(120),
  series: EditorialSeriesSchema,
  /**
   * Para qual peça a imagem é, e com isso QUAL moldura de prompt.
   *
   * São duas molduras, e elas produzem coisas diferentes: `reel` e `story`
   * pedem FUNDO (vertical estreito, terço inferior escuro para o texto caber
   * por cima, cena da série), e `photo` pede a PUBLICAÇÃO INTEIRA (quadrada,
   * composição limpa, cena do assunto).
   *
   * Ele entra aqui, e não vira um segundo adaptador, porque o que muda é o
   * prompt, o tamanho e a qualidade — três parâmetros da mesma chamada. Dois
   * adaptadores duplicariam a classificação de erro, a repetição e a contagem
   * de custo, que são idênticas nos dois casos.
   */
  pieceKind: PieceKindSchema,
  /**
   * O SLIDE do carrossel para o qual a imagem é, quando ela é de um.
   *
   * Ele existe desde 2026-09-07, com o pedido do dono de fotografia em todo
   * slide: "o carrosel eu queria sempre em imagem realística e quando não
   * houver pessoas junto queria um realista 3d". Sem ele, as sete chamadas do
   * carrossel nasceriam todas da mesma pauta e do mesmo gancho, e a peça sairia
   * com sete fundos praticamente iguais — pagando sete vezes por uma imagem só.
   *
   * Ele carrega DUAS coisas, e cada uma decide algo diferente do outro lado:
   *
   * - `role` decide a MOLDURA. A capa pede cena com pessoa (é a miniatura na
   *   grade do perfil, e é ela que para o polegar); a dica e o fechamento pedem
   *   o objeto, a tela ou o fluxo de que o slide fala, sem gente — e por isso
   *   caem na regra do 3D fotorrealista.
   * - `text` é o CONTEXTO da cena, e é o que faz a imagem do slide 3 não ser a
   *   do slide 5.
   *
   * `text` é campo variável como `topicTitle` e `hook`, e vale a mesma cerca:
   * ele já passou pelo `GeneratedContentSchema`, entra CORTADO, e é dado e
   * nunca instrução. Um campo aberto aqui seria uma segunda porta para texto de
   * fonte chegar ao provedor sem passar pelas cercas da geração.
   *
   * Ausente em tudo que não é slide de carrossel, que é o estado das outras
   * quatro chamadas de imagem da esteira.
   */
  slide: z
    .strictObject({
      position: z.number().int().min(1).max(CAROUSEL_SLIDES),
      role: z.enum(CAROUSEL_SLIDE_ROLES),
      text: z.string().trim().min(1).max(IMAGE_SLIDE_TEXT_MAX),
    })
    .optional(),
});
export type ImageGenerationInput = z.infer<typeof ImageGenerationInputSchema>;

/**
 * O que volta de uma geração de imagem.
 *
 * `outputTokens` e `costMicros` são ANULÁVEIS pelo mesmo motivo do gerador de
 * texto: provedor que não devolve uso, e preço não configurado, são
 * "desconhecido", e desconhecido não é zero. Zero aqui viraria um relatório de
 * gasto somando uma conta que ninguém mediu.
 */
export interface ImageGenerationResult {
  readonly provider: string;
  model: string;
  /** O prompt inteiro, do jeito que foi mandado. Ele é registrado. */
  prompt: string;
  promptSha256: string;
  size: string;
  quality: string;
  /** Os bytes do JPEG, já decodificados do base64 da resposta. */
  bytes: Uint8Array;
  outputTokens: number | null;
  costMicros: number | null;
  latencyMs: number;
}

/**
 * O adaptador de imagem.
 *
 * Existe pelo mesmo motivo de `TextGenerator`: a esteira fala com a porta, e
 * quem escolhe o provedor é quem monta o processo. É isso que permite provar a
 * costura inteira — regra do dia, arquivo em disco, registro, spec, inventário
 * de direitos e manifesto — sem uma única chamada paga.
 */
export interface ImageGenerator {
  readonly provider: string;
  generate(input: ImageGenerationInput): Promise<ImageGenerationResult>;
}
